import { useMemo } from 'react';

import { useSession } from '@/context/SessionContext';
import { useTenantFeatureMap } from './useTenantFeatureMap';

const GLOBAL_NOTIFICATION_PERMISSIONS = ['platform.manage'];
const TENANT_NOTIFICATION_PERMISSIONS = ['tenant.notifications.manage'];

const normalize = (value: string) => value.trim().toLowerCase();

export interface NotificationAccessState {
  featureTenantId?: string;
  notificationPermissionGranted: boolean;
  accessEnabled: boolean;
}

export function useNotificationAccess(): NotificationAccessState {
  const { session } = useSession();
  const { featureTenantId } = useTenantFeatureMap();

  return useMemo(() => {
    const permissionSummary = session?.permissionSummary;
    const scopes = session?.scopes ?? [];

    const hasGlobalPermission = permissionSummary?.global?.some(perm =>
      GLOBAL_NOTIFICATION_PERMISSIONS.some(target => normalize(target) === normalize(perm))
    );

    const hasTenantPermission = featureTenantId
      ? permissionSummary?.tenants?.[featureTenantId]?.some(perm =>
          TENANT_NOTIFICATION_PERMISSIONS.some(target => normalize(target) === normalize(perm))
        )
      : false;

    const accessFromScopes = scopes.some(scope => {
      const normalized = normalize(scope);
      return (
        TENANT_NOTIFICATION_PERMISSIONS.some(target => normalized === normalize(target)) ||
        GLOBAL_NOTIFICATION_PERMISSIONS.some(target => normalized === normalize(target))
      );
    });

    const notificationPermissionGranted = Boolean(
      hasGlobalPermission || hasTenantPermission || accessFromScopes
    );

    return {
      featureTenantId,
      notificationPermissionGranted,
      accessEnabled: notificationPermissionGranted
    };
  }, [featureTenantId, session?.permissionSummary, session?.scopes]);
}
